/**
 * TR-01 and TR-02: the two translation-axis lexical-preference channels (T2 design §2.1, §4.2; DESIGN CD-44, CD-46).
 *
 * This file holds the channel DEFINITIONS only; the measurement is `tr01.ts` / `tr02.ts` over `tr-scan.ts`. The definitions live apart
 * from the measurement so that the model loader can fill a translation model's `channels` block for ids the shipped
 * authorship model does not name (`ensemble/model.ts`) without importing any lexicon.
 *
 * Both are rate channels on the translation axis, outside every authorship aggregate by axis (`seed-rules.ts`,
 * `bootstrap.ts`) and outside the legacy-only totals by `LEGACY_CHANNEL_IDS`.
 */
import { derivePrior } from './framework.ts';
import type { ChannelDef, Ramp } from './types.ts';

/**
 * Display ramps (rate per 1,000 words of the unshrunk, log1p-compressed weighted marker sum → 0–100), fitted by C2's
 * convention (`derivePrior`, ENGINE-SPEC §3.7): 20 at the human-typical rate, read off the human reference. The reference is
 * every human original (H) and human translation (HT) CV document with at least 300 prose words (2,775 documents: 2,249 H,
 * 526 HT), their rates computed with the shipped lexicon `tr-markers-8d257b62` (`tools/t2/fit_tr_ramps.py`, output
 * `data/t2/tr-ramps.json`; the gram dump's rates equal the engine's, T-EVAL-10). Anchors, human-reference quantile → score
 * (50 is the rate that 95% of human documents stay below, not a probability):
 *   TR-01 (higher is machine-leaning): 0 → 5, p10 → 10, p50 → 20, p90 → 40, p95 → 50, p99 → 70, p99.9 → 85, then 95 at 1.2 × p99.9
 *     (the ramp keeps rising across the machine range instead of saturating at the top of the human range)
 *   TR-02 (lower is machine-leaning):  0 → 90, p0.1 → 85, p1 → 70, p5 → 50, p10 → 40, p50 → 20, p90 → 10, p99 → 8
 * The prior is derived from the ramp: μ = the rate at 20 (TR-01 54.47, TR-02 45.71), m = 3/μ clamped to the words range.
 * Not calibrated: the calibrated translation model (B5) is what turns these into a claim.
 */
const RAMP_TR01: Ramp = [
	[0, 5],
	[35.89, 10],
	[54.47, 20],
	[66.29, 40],
	[69.17, 50],
	[75.92, 70],
	[83.56, 85],
	[100.28, 95],
];
const RAMP_TR02: Ramp = [
	[0, 90],
	[8.35, 85],
	[24.51, 70],
	[31.3, 50],
	[34.7, 40],
	[45.71, 20],
	[54.73, 10],
	[66.75, 8],
];

/** TR-01: the machine-leaning side of the lexical-preference pair (direction +1). */
export const tr01Def: ChannelDef = {
	id: 'TR-01',
	name: 'Machine-leaning lexical choices',
	family: 'TRANSLATION',
	axis: 'translation',
	registers: ['prose'],
	direction: 1,
	oneWay: false,
	estimator: 'rate',
	features: [
		{ id: 'rate', label: 'Weighted machine-leaning marker sum per 1,000 words' },
		{ id: 'markers', label: 'Distinct markers matched' },
		{ id: 'strong', label: 'Strong machine-leaning markers (evidence spans)' },
	],
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP_TR01,
	unitScale: 1000,
	unitLabel: 'per 1k words (weighted)',
	prior: derivePrior(RAMP_TR01, 1000, 'words', 1.5),
	// T2: unshrunk so `est` = 1000·K/E is reproducible by the trainer from per-document capped gram counts inside nested
	// CV (train/serve parity, T-EVAL-10); κ stays the data weight.
	shrink: false,
	weight: 1,
	nd: 1,
	changes: [],
};

/** TR-02: the human-leaning side of the pair (direction −1). */
export const tr02Def: ChannelDef = {
	id: 'TR-02',
	name: 'Human-leaning lexical choices',
	family: 'TRANSLATION',
	axis: 'translation',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'rate',
	features: [
		{ id: 'rate', label: 'Weighted human-leaning marker sum per 1,000 words' },
		{ id: 'markers', label: 'Distinct markers matched' },
		{ id: 'strong', label: 'Strong human-leaning markers (evidence spans)' },
	],
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP_TR02,
	unitScale: 1000,
	unitLabel: 'per 1k words (weighted)',
	prior: derivePrior(RAMP_TR02, 1000, 'words', 1.5),
	// T2: unshrunk so `est` = 1000·K/E is reproducible by the trainer from per-document capped gram counts inside nested
	// CV (train/serve parity, T-EVAL-10); κ stays the data weight.
	shrink: false,
	weight: 1,
	nd: 1,
	changes: [],
};

/** The T2 definitions by id (no implementation until B3). */
export const TR_DEFS = { 'TR-01': tr01Def, 'TR-02': tr02Def } as const;
